import { MoneyCollectOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Table,
  Tooltip,
} from 'antd';
import React, { useState } from 'react';
import { OrderV2 } from './OrderListV2/types';
import { fetchAPI } from '../../utils/api';
import { useQuery } from '@tanstack/react-query';
import { FIVE_MINUTES } from '../../constants/CONSTANTS';
import { UserActivityItem } from './OrderInvoiceDateHistory';
import moment from 'moment';
import { safelyParseJSON } from '../Ticketing/SubmittedTicketList';
import { formatUTCTimeToLocal } from '../../utils/format';
import { OrderPaymentStatusTag } from './OrderPaymentStatus';

const pageSize = 10;

type PartialPayment = {
  order_id: number; // 7009,
  value: number; // 1000,
  reference_doc_no: string; // "SS-PP-123456",
  payment_date: string; // "2025-10-01",
  user_id: number; // 51,
  next_status: string; // "PARTIALLY_PAID"
};

const OrderPartialPaymentRecords = (props: {
  record: OrderV2;
  onOpenChange?: (open: boolean) => void;
}) => {
  const [visible, setVisible] = useState(false);
  const [newRecord, setNewRecord] = useState({
    visible: false,
    value: null,
    date: null,
    reference_doc_no: null,
    loading: false,
  });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const orderText =
    props.record.po_reference_number ||
    props.record.region_ref_number ||
    props.record.id;

  const addNewRecord = async () => {
    setNewRecord(nr => ({ ...nr, loading: true }));
    try {
      await fetchAPI('order/payment/record', {
        method: 'POST',
        payload: {
          order_id: props.record.id,
          value: newRecord.value,
          reference_doc_no: newRecord.reference_doc_no,
          payment_date: newRecord.date.format('YYYY-MM-DD'),
        },
      });
      message.success('New Partial Payment Saved!');
      setNewRecord(nr => ({ ...nr, visible: false }));
      // refresh list
      setLoading(true);
      await ppRecords.refetch();
      setLoading(false);
    } catch (error) {
      message.error('Failed add new partial payment record');
      if (error.code) {
        message.error(error.code);
      }
      if (error.message) {
        message.error(error.message);
      }
    }
    setNewRecord(nr => ({ ...nr, loading: false }));
  };

  const ppRecords = useQuery<{
    loading: boolean;
    items: UserActivityItem[];
    page: number;
    per_page: number;
    total: number;
  }>(
    ['partial-payment-record', props.record?.id, page, visible],
    async () => {
      if (!visible || !props.record?.id) {
        return {
          loading: false,
          items: [],
          page,
          per_page: pageSize,
          total: 0,
        };
      }
      try {
        const resp = await fetchAPI(`user/activity`, {
          payload: {
            page: page,
            per_page: pageSize,
            table_name: 'order',
            row_id: props.record.id,
            value_type: 'partial_payment_said',
          },
        });
        return resp;
      } catch (error) {
        message.error('Failed get partial payment records');
        return {
          items: [],
          page,
          per_page: pageSize,
          total: 0,
          loading: false,
        };
      }
    },
    {
      staleTime: FIVE_MINUTES,
      placeholderData: {
        loading: true,
        items: [],
        page,
        per_page: pageSize,
        total: 0,
      },
    }
  );

  return (
    <>
      <Tooltip title="Order Partial Payment Records">
        <Button
          icon={<MoneyCollectOutlined />}
          onClick={() => {
            // Close dropdown immediately when clicked
            props.onOpenChange?.(false);
            setVisible(true);
          }}
        >
          Partial Payment
        </Button>
      </Tooltip>
      <Modal
        title={`Partial Payment Records (${orderText})`}
        visible={visible}
        onCancel={() => setVisible(false)}
        width={1000}
      >
        <div>
          <div style={{ textAlign: 'right', marginBottom: 15 }}>
            <Button
              icon={<PlusOutlined />}
              type="primary"
              onClick={() =>
                setNewRecord({
                  visible: true,
                  value: null,
                  date: null,
                  reference_doc_no: null,
                  loading: false,
                })
              }
            >
              Add Partial Payment Record
            </Button>
          </div>
          <div>
            <Table
              size="small"
              dataSource={ppRecords.data.items}
              pagination={{
                pageSize,
                current: page,
                onChange: p => setPage(p),
              }}
              loading={loading || ppRecords.data.loading}
              columns={[
                {
                  title: 'Payment Date',
                  key: 'payment-date',
                  render: (t, record) => {
                    const obj: PartialPayment = safelyParseJSON(
                      record.new_value
                    );
                    return !!obj.payment_date
                      ? moment(obj.payment_date).format('DD MMM YYYY')
                      : '-';
                  },
                },
                {
                  title: 'Document Reference No',
                  key: 'docs-ref-no',
                  render: (t, record) => {
                    const obj: PartialPayment = safelyParseJSON(
                      record.new_value
                    );
                    return obj.reference_doc_no || '-';
                  },
                },
                {
                  title: 'Payment Amount',
                  key: 'payment-amount',
                  render: (t, record) => {
                    const obj: PartialPayment = safelyParseJSON(
                      record.new_value
                    );
                    return (obj.value || 0).toLocaleString();
                  },
                },
                {
                  title: 'Total Payment',
                  key: 'total-payment',
                  render: (t, record) => {
                    const obj: PartialPayment = safelyParseJSON(
                      record.new_value
                    );
                    return (
                      (obj.value || 0) + (Number(record.old_value) || 0)
                    ).toLocaleString();
                  },
                },
                {
                  title: 'Next SAID Status',
                  key: 'next-said-status',
                  render: (t, record) => {
                    const obj: PartialPayment = safelyParseJSON(
                      record.new_value
                    );

                    return (
                      <OrderPaymentStatusTag paymentStatus={obj.next_status} />
                    );
                  },
                },
                {
                  title: 'Created by',
                  key: 'created-by',
                  render: (t, record) => {
                    return record.user?.full_name;
                  },
                },
                {
                  title: 'Created at',
                  key: 'created-at',
                  render: (t, record) => {
                    return formatUTCTimeToLocal(
                      record.created_at,
                      'HH:mm DD MMM YYYY'
                    );
                  },
                },
              ]}
            />
          </div>
        </div>

        {/* MODAL NEW PARTIAL PAYMENT RECORD */}
        <Modal
          title={`New Partial Payment Record (${orderText})`}
          visible={newRecord.visible}
          onCancel={() =>
            !newRecord.loading &&
            setNewRecord(nr => ({ ...nr, visible: false }))
          }
          destroyOnClose
          okButtonProps={{
            disabled:
              !newRecord.date ||
              !newRecord.reference_doc_no ||
              !newRecord.value,
            loading: newRecord.loading,
            onClick: () => addNewRecord(),
          }}
          cancelButtonProps={{ disabled: newRecord.loading }}
        >
          <Form layout="vertical" style={{ maxWidth: 300 }}>
            <Form.Item label="Payment Document Reference Number" required>
              <Input
                value={newRecord.reference_doc_no}
                onChange={e => {
                  e.persist();
                  setNewRecord(nr => ({
                    ...nr,
                    reference_doc_no: e.target?.value || '',
                  }));
                }}
                placeholder="Document reference number"
                disabled={newRecord.loading}
              />
            </Form.Item>
            <Form.Item label="Payment Value" required>
              <InputNumber
                placeholder="Value of partial payment"
                value={newRecord.value}
                onChange={val => setNewRecord(nr => ({ ...nr, value: val }))}
                disabled={newRecord.loading}
                style={{ width: '100%' }}
              />
            </Form.Item>
            <Form.Item label="Payment Date" required>
              <DatePicker
                format={'DD MMM YYYY'}
                onChange={val => setNewRecord(nr => ({ ...nr, date: val }))}
                value={newRecord.date}
                placeholder="Select payment date"
                disabled={newRecord.loading}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Form>
        </Modal>
      </Modal>
    </>
  );
};

export default OrderPartialPaymentRecords;
